import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { AppointmentApiService } from '@api_patient/appointment_api.service';
import { signal } from '@preact/signals-react';
import { AsyncSignalValue } from '@component/async.component';
import { ExoticRespondantStatusApiModel, FormManager } from '@feature/dynamic_form/form_manager/form_manager.service';
import { FormOpportunity } from '@api_patient/generated/clientv2/models/form-opportunity';
import { States as PaymentState } from '@api_patient/generated/clientv2/models/states';
import type { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import { GeneralApiService } from '@api_patient/general_api.service';
import { RouterService } from '@cortex-shim/router.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { DateService } from '@application/time/date.service';
import { UniversalConfig } from '@application/universal_config.service';
import { OfficeApiService } from '@api_patient/office_api.service';
import { Logger } from '@visiba/logger';
import { type LeaveType } from '@visiba/retina';

interface AppointmentState {
	beforeForm: Nullable<Readonly<FormWorker>>;
	afterForm: Nullable<Readonly<FormWorker>>;
	shouldEvaluate: Nullable<'completed' | 'not_completed'>;
	isMissingPayment: boolean;
	reconnectVariants: Nullable<LeaveType>;
}

interface AppointmentInfo {
	bookingId: number;
	resourceId: Nullable<number>;
	isHcp: boolean;
	isGuest: boolean;
	isHost: boolean;
	otherParticipants: string[];
	time: {
		start: string;
		end: string;
	};
	vonageEncryptionRequired: boolean;
	proxyUrl: Nullable<string>;
}

@Injectable()
export class AppointmentController implements ControllerProtocol {
	public logger = new Logger('Retina');
	public savedTicket: Nullable<string> = null;

	public readonly appointmentInfo = signal<Nullable<AppointmentInfo>>(null);

	public readonly state = signal<AsyncSignalValue<AppointmentState>>({
		value: null,
		flowState: 'pending',
	});

	constructor(
		public readonly formManager: FormManager,
		private readonly appointmentApiService: AppointmentApiService,
		private readonly officeApiService: OfficeApiService,
		private readonly generalApiService: GeneralApiService,
		private readonly routerService: RouterService,
		private readonly dateService: DateService,
		private readonly universalConfig: UniversalConfig,
	) {}

	public callStatus(callticket: string) {
		return this.appointmentApiService.callStatus(callticket);
	}

	async whenMount(callticket: Nullablish<string>) {
		if (callticket == null) return;

		this.savedTicket = callticket;

		const [result, accessInfo] = await Promise.all([
			this.appointmentApiService.get(callticket),
			this.generalApiService.getAppointmentAccessInfo(callticket),
		]);

		if (accessInfo.err || result.err) {
			return;
		}

		const start = this.dateService.newDate(result.ok.data.Start);

		this.appointmentInfo.value = {
			bookingId: result.ok.data.BookingId,
			resourceId: result.ok.data.ResourceId,
			isHost: result.ok.data.Role === 'Host',
			isHcp: accessInfo.ok.data.ResourceId != null,
			isGuest: accessInfo.ok.data.OutsiderId != null,
			otherParticipants: result.ok.data.OtherParticipants.map((x) => x.Name ?? I18nAndL10n.translate('PW.Common.Anonymous')),
			time: {
				start: start.format('HH:mm'),
				end: start.add(result.ok.data.Duration, 'minutes').format('HH:mm'),
			},
			vonageEncryptionRequired: result.ok.data.VonageEncryptionRequired,
			proxyUrl: result.ok.data.VonageProxyUrl,
		};

		if (result.ok) {
			const appointment = result.ok.data;
			const beforeForms = appointment.Forms.filter((x) => x.Opportunity === FormOpportunity.BeforeBooking).map((x) => ({
				formId: x.FormId,
				completed: x.Completed,
				header: x.Header ?? '',
				issueTrackingId: '',
				opportunity: x.Opportunity,
				respondantId: x.RespondantId,
				dataProvider: {
					type: 'respondant',
					respondantId: x.RespondantId,
				},
			})) satisfies ExoticRespondantStatusApiModel[];

			const afterForms = appointment.Forms.filter((x) => x.Opportunity === FormOpportunity.AfterBooking).map((x) => ({
				formId: x.FormId,
				completed: x.Completed,
				header: x.Header ?? '',
				issueTrackingId: '',
				opportunity: x.Opportunity,
				respondantId: x.RespondantId,
				dataProvider: {
					type: 'respondant',
					respondantId: x.RespondantId,
				},
			})) satisfies ExoticRespondantStatusApiModel[];

			this.formManager.addRespondants(beforeForms);
			this.formManager.addRespondants(afterForms);

			this.state.value = {
				value: {
					reconnectVariants: null,
					isMissingPayment: appointment.Payment != null && appointment.Payment.State !== PaymentState.Paid,
					beforeForm: this.formManager.getNextUncompletedForm({
						opportunity: FormOpportunity.BeforeBooking,
					}),
					shouldEvaluate: null,
					afterForm: null,
				},
				flowState: 'accessible',
			};
		}
	}

	public async handleForceEnd() {
		const prev = this.state.peek();

		const appointmentInfo = this.appointmentInfo.peek();
		const bookingId = appointmentInfo?.bookingId ?? -1;
		const licenceHolderId = this.universalConfig.licenceHolderId;

		await this.officeApiService.setCompleted(licenceHolderId, bookingId);

		this.state.value = {
			value: {
				...prev.value!,
				reconnectVariants: null,
				shouldEvaluate: 'not_completed',
			},
			flowState: 'accessible',
		};
	}

	public handleReconnect() {
		const prev = this.state.peek();

		this.state.value = {
			value: {
				...prev.value!,
				reconnectVariants: null,
				shouldEvaluate: null,
				afterForm: null,
			},
			flowState: 'accessible',
		};
	}

	public handleForceEvaluation() {
		const prev = this.state.peek();

		this.state.value = {
			value: {
				...prev.value!,
				reconnectVariants: null,
				shouldEvaluate: 'not_completed',
			},
			flowState: 'accessible',
		};
	}

	public handleAfterRetinaLeave(leaveType: LeaveType) {
		let leaveAction: Nullable<LeaveType> = leaveType;
		// We don't want to show any addiation flow for these leave types.
		if (leaveType === 'shouldEnd' || leaveType === 'shouldLeave') {
			leaveAction = null;
		}

		this.logger.logVerbose('Leavetype {type}', leaveType);

		this.state.value = {
			value: {
				reconnectVariants: leaveAction,
				isMissingPayment: false,
				beforeForm: null,
				shouldEvaluate: 'not_completed',
				afterForm: this.formManager.getNextUncompletedForm({
					opportunity: FormOpportunity.AfterBooking,
				}),
			},
			flowState: 'accessible',
		};
	}

	public handleSurveyEnd() {
		this.state.value = {
			value: {
				reconnectVariants: null,
				isMissingPayment: false,
				beforeForm: null,
				shouldEvaluate: 'completed',
				afterForm: this.formManager.getNextUncompletedForm({
					opportunity: FormOpportunity.AfterBooking,
				}),
			},
			flowState: 'accessible',
		};
	}

	public async handleCallStarted() {
		// When the call starts. We need to refetch the already populated data
		// so we may read "afterBooking" again. These may or may not have been added.
		// Ideally this should be a slimmed down request.

		if (this.savedTicket == null) return;

		this.logger.logVerbose('Fetches for afterBooking forms');
		const result = await this.appointmentApiService.get(this.savedTicket);
		if (result.err != null) return;

		const afterForms = result.ok.data.Forms.filter((x) => x.Opportunity === FormOpportunity.AfterBooking).map((x) => ({
			formId: x.FormId,
			completed: x.Completed,
			header: x.Header ?? '',
			issueTrackingId: '',
			opportunity: x.Opportunity,
			respondantId: x.RespondantId,
			dataProvider: {
				type: 'respondant',
				respondantId: x.RespondantId,
			},
		})) satisfies ExoticRespondantStatusApiModel[];

		this.formManager.addRespondants(afterForms);
	}

	public changeForm(opportunity: FormOpportunity) {
		const prevState = this.state.peek().value;

		const nextForm = this.formManager.getNextUncompletedForm({
			opportunity: opportunity,
		});

		this.state.value = {
			value: {
				reconnectVariants: null,
				isMissingPayment: false,
				beforeForm: opportunity === FormOpportunity.BeforeBooking ? nextForm : (prevState?.beforeForm ?? null),
				shouldEvaluate: prevState?.shouldEvaluate ?? null,
				afterForm: opportunity === FormOpportunity.AfterBooking ? nextForm : (prevState?.afterForm ?? null),
			},
			flowState: 'accessible',
		};
	}

	public getFormPosition(worker: Readonly<FormWorker>): number {
		const allForms = this.formManager.filterForms({
			opportunity: worker.opportunity,
		});
		const formIndex = allForms.findIndex((x) => x.formId === worker.formId);
		const prevForm = formIndex - 1;
		if (prevForm < 0) return -1;

		return prevForm;
	}

	public changeFormByIndex(opportunity: FormOpportunity, index: number) {
		const allForms = this.formManager.filterForms({
			opportunity: opportunity,
		});

		const nextForm = allForms[index];
		if (nextForm == null) return;

		const prevState = this.state.peek().value;

		this.state.value = {
			value: {
				reconnectVariants: null,
				isMissingPayment: false,
				beforeForm: opportunity === FormOpportunity.BeforeBooking ? nextForm : (prevState?.beforeForm ?? null),
				shouldEvaluate: prevState?.shouldEvaluate ?? null,
				afterForm: opportunity === FormOpportunity.AfterBooking ? nextForm : (prevState?.afterForm ?? null),
			},
			flowState: 'accessible',
		};
	}

	public handleBack() {
		const route = this.universalConfig.alias + '/visits' + (this.savedTicket != null ? `/video/${this.savedTicket}` : '');

		this.routerService.navigateByUrl(route);
	}
}
